// components/portal/PortalHomeMenu.tsx
'use client';

import {
  useEffect,
  useMemo,
  useCallback,
  useState,
  useRef,
  memo,
  type ReactNode,
} from 'react';
import { useKernel } from '@/kernel/Kernel';
import { type PortalView, type StudioSection, type LeasingOrganisationDTO } from '#types/types';
import { useKernelStore } from '@/kernel/kernelStores';
import { toggleMasterSessionPin } from '@/lib/masterSessionPins';
import { confirmChatSessionDeletion } from '#lib/chat/chatSessionDeletion';
import { readDevicePreference, writeDevicePreference } from '#lib/devicePreferences';
import {
  BookOpen,
  Presentation,
  Box,
  ChevronDown,
  Home,
  MoreHorizontal,
  PencilLine,
  Pin,
  PinOff,
  Share2,
  Trash2,
  Funnel,
  Map as MapIcon,
  Table2,
  Database,
  Activity,
  Search as SearchIcon,
  SquarePen,
} from '@/components/ui/lucide-memo';
import { StudioCanvasIcon, StudioListIcon, StudioVectorIcon } from '@/components/ui/CustomIcons';
import FaterLogo from '@/components/ui/FaterLogo';
import { DaimonOrb } from '@/components/daimon/DaimonOrb';
import { useDaimonContext } from '@/components/daimon/DaimonVoice';
import { PortalAccountCorner } from '@/components/portal/PortalAccountCorner';
import { canAccessDaimon } from '#lib/access/daimonAccess';
import clsx from 'clsx';
import { isTableFocusSubject, resolvePortalViewFromTableFocus, selectActiveSubjectFromInventoryState } from '#lib/leasing/session-focus';
import { MASTER_AGENT_COMING_SOON_LABEL } from '#lib/access/masterAgentAccess';
import { SLIDES_TRIAL_LABEL } from '@/components/slides/SlidesTrialNotice';
import { SOURCE_TRIAL_LABEL } from '@/components/portal/SourceTrialNotice';
import { agentIsWorking } from '#lib/chat/agentIsWorking';
import type { ChatStateItem, StudioProjectKind } from '#types/types';
import { canAccessStudioKind } from '#lib/studio-project-view';

// Ordered ids for the left-bar "Your chats" list: the current user's MASTER
// sessions (creationModule 'master'), newest first. Shown once they have a
// message, PLUS the currently-active session even when it is brand-new (0
// messages) so a freshly created session appears in the list live. Studio/Source/
// Analyst conversations live in their tool projects, not here.
const buildGlobalChatSessionIds = (
  registry: Record<string, { id: string; ownerId: string | null; messageCount?: number; updatedAt?: string; createdAt?: string; creationModule?: string }>,
  currentUserId: string | null | undefined,
  activeSessionId?: string | null,
): string[] => {
  const uid = typeof currentUserId === 'string' ? currentUserId.trim() : '';
  if (!uid) return [];
  return Object.values(registry)
    .filter((s) =>
      (typeof s.ownerId === 'string' ? s.ownerId.trim() : '') === uid &&
      // Daimon conversations sit in the same Recents, as ChatGPT lists every chat (owner, 2026-09-28).
      (s.creationModule === 'master' || s.creationModule === 'daimon') &&
      ((s.messageCount ?? 0) > 0 || s.id === activeSessionId)
    )
    .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0).getTime() - new Date(a.updatedAt || a.createdAt || 0).getTime())
    .map((s) => s.id);
};

// The expanded sidebar width is user-resizable (drag the right edge) and remembered per
// device, not per user: it is a fit to one physical screen, so carrying a width dragged on
// a 34" ultrawide over to a 13" laptop would be wrong (doctrine 08 §5). Clamped on read so
// it can never get too narrow to read or wide enough to eat the main content.
const SIDEBAR_WIDTH_KEY = 'fater-sidebar-width-v2';
const DEFAULT_SIDEBAR_WIDTH = 260;
const MIN_SIDEBAR_WIDTH = 210;
const MAX_SIDEBAR_WIDTH = 460;
const clampSidebarWidth = (w: number): number =>
  Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, Math.round(w)));
const readSidebarWidth = (): number => {
  const stored = readDevicePreference('portalHomeMenuWidth');
  return stored === null ? DEFAULT_SIDEBAR_WIDTH : clampSidebarWidth(stored);
};
const writeSidebarWidth = (w: number): void => writeDevicePreference('portalHomeMenuWidth', clampSidebarWidth(w));

// A single row in the left-bar chat list. Reads its own session from the store so
// rows re-render granularly. A single click opens the chat; a double click renames it
// inline; the hover "..." (or a right-click) opens rename / delete / share.
const PortalHomeChatSessionItem = memo(({
  sessionId,
  isActive,
  onOpenSession,
  onSessionContextMenu,
  onRenameSession,
  fontFamily,
}: {
  sessionId: string;
  isActive: boolean;
  onOpenSession: (sessionId: string) => void;
  onSessionContextMenu: (sessionId: string, event: React.MouseEvent) => void;
  onRenameSession: (sessionId: string, name: string) => void;
  fontFamily: string;
}) => {
  const session = useKernelStore((state) => state.inventory.ChatSession.registry[sessionId] ?? null);
  // Is this session's agent actively running a turn / generation right now? Kept
  // per-session (survives navigating away), so the dot pulses on any working
  // session, not just the one you're viewing.
  const isWorking = useKernelStore((state) => {
    const runtime = state.inventory.ChatSession.registry[sessionId]?.runtime as
      { chatState?: ChatStateItem[]; runningScriptId?: string | null } | undefined;
    return agentIsWorking(runtime?.chatState, runtime?.runningScriptId);
  });
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // Discriminate a single click (open) from a double click (rename): a lone click opens
  // after a short delay; a second click within that window cancels the open and edits.
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (clickTimerRef.current) clearTimeout(clickTimerRef.current); }, []);
  if (!session) return null;
  const label = session.name?.trim() || 'New chat';
  const beginEdit = () => {
    if (clickTimerRef.current) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null; }
    setDraft(session.name?.trim() || '');
    setIsEditing(true);
  };
  const commitEdit = () => {
    const name = draft.trim();
    if (name && name !== (session.name?.trim() || '')) onRenameSession(session.id, name);
    setIsEditing(false);
  };
  const handleRowClick = () => {
    if (clickTimerRef.current) return;
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      onOpenSession(session.id);
    }, 200);
  };
  if (isEditing) {
    return (
      <div className="flex h-9 w-full items-center rounded-[10px] bg-[#2A2A2A] px-3" style={{ fontFamily }}>
        <input
          autoFocus
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onFocus={(event) => event.currentTarget.select()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') { event.preventDefault(); commitEdit(); }
            else if (event.key === 'Escape') { event.preventDefault(); setIsEditing(false); }
          }}
          onBlur={commitEdit}
          className="w-full bg-transparent text-white outline-none"
          style={{ fontSize: '14px', lineHeight: '14px', fontFamily }}
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={handleRowClick}
      onDoubleClick={beginEdit}
      onContextMenu={(event) => onSessionContextMenu(session.id, event)}
      className={clsx(
        'group flex h-9 w-full items-center justify-between rounded-[10px] px-2.5 text-left text-white transition-colors',
        isActive ? 'bg-[#303030]' : 'hover:bg-[#303030]',
      )}
      style={{ fontFamily }}
      title={label}
    >
      <span className="flex min-w-0 items-center gap-2">
        {isWorking && (
          <span
            aria-hidden
            className={clsx(isWorking && 'animate-pulse')}
            style={{ width: 7, height: 7, borderRadius: 9999, background: '#ECECEC', flexShrink: 0 }}
          />
        )}
        <span className="truncate" style={{ fontSize: '14px', lineHeight: '20px', letterSpacing: '-0.2px', fontFamily }}>
          {label}
        </span>
      </span>
      {/* ChatGPT's row: the title runs to the edge; the "..." (rename, pin, delete) takes its place
          only on hover and on the open chat, so a hidden control never shortens the title. */}
      <span className={clsx('ml-2 shrink-0 items-center', isActive ? 'flex' : 'hidden group-hover:flex')}>
        <span
          role="button"
          tabIndex={-1}
          onClick={(event) => { event.stopPropagation(); onSessionContextMenu(session.id, event); }}
          onDoubleClick={(event) => event.stopPropagation()}
          className={clsx(
            'cursor-pointer rounded p-0.5 text-[#B5B5B5] hover:text-white',
          )}
          aria-label="Chat options"
        >
          <MoreHorizontal size={16} />
        </span>
      </span>
    </button>
  );
});
PortalHomeChatSessionItem.displayName = 'PortalHomeChatSessionItem';

const LEFTBAR_FONT_FAMILY = '-apple-system-body, ui-sans-serif, -apple-system, system-ui, "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"';

const SectionHeading = ({ label, open, onToggle }: { label: string; open: boolean; onToggle: () => void }) => (
  <button
    type="button"
    onClick={onToggle}
    className="group/heading flex h-9 w-full items-center gap-1 rounded-[10px] px-2.5 text-[#AFAFAF]"
    aria-expanded={open}
    aria-label={`${open ? 'Collapse' : 'Expand'} ${label.toLowerCase()}`}
    style={{ fontFamily: LEFTBAR_FONT_FAMILY }}
  >
    <span className="tracking-[-0.01em]" style={{ fontSize: '14px', lineHeight: '20px', letterSpacing: '-0.2px', fontFamily: LEFTBAR_FONT_FAMILY }}>
      {label}
    </span>
    <ChevronDown
      size={16}
      strokeWidth={1.9}
      className={clsx('opacity-0 transition-[opacity,transform] duration-200 group-hover/heading:opacity-100', open ? 'rotate-0' : '-rotate-90')}
    />
  </button>
);

export const PortalHomeMenu = memo(({
  onNewChat,
  onSearchChats,
  onNavigateToView,
  onSource,
  activeChatSessionId,
  isFaterAdmin,
  masterAgentUnlocked,
  canAccessAssetManagement,
  canAccessSource,
  canAccessFootfall,
  canAccessDesign,
  canAccessSlidesTool,
  canAccessFater3D = false,
  isSlidesTrial,
  isSourceTrial,
  isCollapsed,
  onToggleCollapse,
  activeStandaloneView,
  onOpenSession,
  onGoHome,
  showDivider = true,
}: {
  onNewChat: () => void;
  onSearchChats: () => void;
  onNavigateToView: (view: PortalView, studioDestination?: StudioProjectKind) => void;
  onSource: () => void;
  onOpenOrganisationPage: () => void;
  organisations: Pick<LeasingOrganisationDTO, 'id' | 'name'>[];
  selectedOrganisationId: string | null;
  onSelectOrganisation: (orgId: string) => void;
  organisationName: string;
  activeChatSessionId: string | null;
  isFaterAdmin: boolean;
  /** Fater admins only: keeps the unreleased Master agent usable while it is built. */
  masterAgentUnlocked: boolean;
  canAccessAssetManagement: boolean;
  canAccessSource: boolean;
  canAccessFootfall?: boolean;
  canAccessDesign: boolean;
  /** Slides pilot gate, see lib/access/slidesAccess.ts. */
  canAccessSlidesTool: boolean;
  /** Fater 3D pilot gate, see lib/access/fater3dAccess.ts. */
  canAccessFater3D?: boolean;
  isSlidesTrial: boolean;
  isSourceTrial: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  activeStandaloneView?: PortalView | null;
  onOpenSession: (sessionId: string) => void;
  onGoHome: () => void;
  // The left divider is the SINGLE vertical stroke for boxless surfaces (hero,
  // plain/master chat). On boxed surfaces the curved box already draws the left
  // edge, so we drop this to avoid a redundant double line at x=68.
  showDivider?: boolean;
}) => {
  // CHATGPT'S SIDEBAR, MEASURED ON chatgpt.com (2026-09-30, owner: 'pixel perfect'): a 260px bar; a
  // 52px header, the name at 18px, 36px icon buttons 8px from the right; rows from y=60, 248x36, 6px
  // from each edge, 10px padding and radius, a 20px icon at 16px, 6px gap, the label at 42px in 14/20
  // with -0.2px tracking; hover #303030; section titles and chat titles at 16px.
  const ICON_SIZE = 20;
  const ICON_STROKE = 1.9;
  const [isOrgHovered, setIsOrgHovered] = useState(false);
  const [isPinnedOpen, setIsPinnedOpen] = useState(true);
  const [isQuickAccessOpen, setIsQuickAccessOpen] = useState(true);
  const [isRecentsOpen, setIsRecentsOpen] = useState(true);
  const { kernel } = useKernel();
  // Pinned master-session ids, from User.preferences (stable signature string so the selector
  // doesn't return a fresh Set each render). Kept as a Set for O(1) membership when
  // partitioning the chat list into Pinned and Recents. Reading the store is what keeps the
  // sidebar in sync when a pin is toggled from the conversation menu. Empty until preferences
  // hydrate, which simply leaves everything under Recents for a moment.
  const pinnedSessionIdsSignature = useKernelStore((state) => (state.userPreferences.pinnedMasterSessionIds ?? []).join(','));
  const pinnedSessionIds = useMemo(
    () => new Set(pinnedSessionIdsSignature ? pinnedSessionIdsSignature.split(',') : []),
    [pinnedSessionIdsSignature],
  );
  const togglePin = useCallback((sessionId: string) => {
    const k = kernel.current;
    if (k) toggleMasterSessionPin(k, sessionId);
  }, [kernel]);
  // Resizable sidebar: drag the right edge to set the expanded width, kept per device (lib/devicePreferences).
  // Seeded from the default and read in an effect, never in the useState initializer: the
  // server always renders DEFAULT_SIDEBAR_WIDTH, so reading the stored value during the first
  // render would desynchronise hydration.
  const [sidebarWidth, setSidebarWidth] = useState<number>(DEFAULT_SIDEBAR_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  // The width is mirrored in a ref so the drag handler reads it at mousedown instead of
  // capturing it as a dependency: it changes on every mousemove of a resize, which would
  // reinstantiate the handler at cursor frequency (doctrine 04).
  const sidebarWidthRef = useRef(DEFAULT_SIDEBAR_WIDTH);
  useEffect(() => {
    const stored = readSidebarWidth();
    sidebarWidthRef.current = stored;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the stored width is read after mount so the server render matches the client's first one
    setSidebarWidth(stored);
  }, []);
  const startSidebarResize = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = sidebarWidthRef.current;
    setIsResizing(true);
    if (typeof document !== 'undefined') {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    }
    const onMove = (ev: MouseEvent) => {
      const next = clampSidebarWidth(startWidth + (ev.clientX - startX));
      sidebarWidthRef.current = next;
      setSidebarWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setIsResizing(false);
      if (typeof document !== 'undefined') {
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      }
      writeSidebarWidth(sidebarWidthRef.current);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, []);
  const resetSidebarWidth = useCallback(() => {
    sidebarWidthRef.current = DEFAULT_SIDEBAR_WIDTH;
    setSidebarWidth(DEFAULT_SIDEBAR_WIDTH);
    writeSidebarWidth(DEFAULT_SIDEBAR_WIDTH);
  }, []);
  // The user's chat sessions for the "Your chats" list (stable signature string so
  // the selector doesn't return a fresh array each render).
  const chatSessionIdsSignature = useKernelStore((state) =>
    buildGlobalChatSessionIds(state.inventory.ChatSession.registry, state.userContext.dbUserId, state.activeChatSessionId).join(',')
  );
  // A Daimon conversation opens on the Daimon page, not in the Master console; the nav's Daimon
  // entry starts a new one.
  const daimonSessionId = useKernelStore((state) => state.daimonSessionId);
  const daimon = useDaimonContext();
  const openSession = useCallback(async (sessionId: string) => {
    const k = kernel.current;
    if (k?.getInventory().ChatSession.registry[sessionId]?.creationModule === 'daimon') {
      try {
        if ((await daimon.openSavedConversation(sessionId)).id) onGoHome();
      } catch (error) { k.handleError(error instanceof Error ? error.message : 'The conversation could not be selected.'); }
      return;
    }
    onOpenSession(sessionId);
  }, [kernel, daimon.openSavedConversation, onGoHome, onOpenSession]);
  const startDaimonConversation = useCallback(async () => {
    try { if (await daimon.newConversation()) onGoHome(); }
    catch (error) { kernel.current?.handleError(error instanceof Error ? error.message : 'The conversation could not be started.'); }
  }, [kernel, daimon.newConversation, onGoHome]);
  const chatSessionIds = useMemo(
    () => (chatSessionIdsSignature ? chatSessionIdsSignature.split(',') : []),
    [chatSessionIdsSignature],
  );
  // Split the chat list into Pinned and Recents. Both preserve the newest-first order
  // from chatSessionIds; pins that point at a deleted session simply drop out here.
  const pinnedChatSessionIds = useMemo(
    () => chatSessionIds.filter((id) => pinnedSessionIds.has(id)),
    [chatSessionIds, pinnedSessionIds],
  );
  const recentChatSessionIds = useMemo(
    () => chatSessionIds.filter((id) => !pinnedSessionIds.has(id)),
    [chatSessionIds, pinnedSessionIds],
  );
  // Whether the master console is the current view (used to light "New session").
  const isMasterSessionActive = useKernelStore((state) => {
    const id = state.activeChatSessionId;
    return !!id && state.inventory.ChatSession.registry[id]?.creationModule === 'master';
  });
  const handleSessionContextMenu = useCallback((sessionId: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const k = kernel.current;
    if (!k) return;
    const session = k.getInventory().ChatSession.registry[sessionId];
    if (!session) return;
    const canShare = typeof navigator !== 'undefined' && !!navigator.clipboard;
    const isPinned = pinnedSessionIds.has(session.id);
    const actions = [
      {
        label: isPinned ? 'Unpin' : 'Pin',
        icon: isPinned ? <PinOff size={18} /> : <Pin size={18} />,
        onClick: () => togglePin(session.id),
      },
      ...(canShare
        ? [{
            label: 'Share',
            icon: <Share2 size={18} />,
            onClick: async () => {
              try {
                await navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}?c=${session.id}`);
              } catch { /* clipboard unavailable */ }
            },
          }]
        : []),
      {
        label: 'Rename',
        icon: <PencilLine size={18} />,
        onClick: () => {
          const newName = window.prompt('Rename chat:', session.name || 'New chat');
          if (newName && newName.trim()) k.renameChatSession(session.id, newName.trim());
        },
      },
      {
        label: 'Delete',
        icon: <Trash2 size={18} />,
        danger: true,
        separatorBefore: true,
        onClick: () => confirmChatSessionDeletion(kernel, session.id),
      },
    ];
    k.setMenuData({ position: { x: event.clientX, y: event.clientY }, actions });
  }, [kernel, pinnedSessionIds, togglePin]);
  const handleRenameSession = useCallback((sessionId: string, name: string) => {
    const trimmed = name.trim();
    if (trimmed) kernel.current?.renameChatSession(sessionId, trimmed);
  }, [kernel]);
  const COLLAPSE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  const activeTableView = useKernelStore((state) =>
    resolvePortalViewFromTableFocus(selectActiveSubjectFromInventoryState(state))
  );
  const studioSection = useKernelStore(state => state.studioSection);
  const canAccessFactory = useKernelStore(state => Boolean(state.userContext.permissions?.canAccessFactory));
  const canAccessVector = useKernelStore(state => canAccessStudioKind('vector', state.userContext.permissions));
  const isProjectSubject = useKernelStore(state => {
    const subject = selectActiveSubjectFromInventoryState(state);
    return Boolean(subject && !isTableFocusSubject(subject) && subject.entityType === 'Project');
  });
  const studioActive = (activeTableView === 'projects' || isProjectSubject) && !isMasterSessionActive && !activeStandaloneView;
  const isSourceActive = useKernelStore((state) => {
    const subject = selectActiveSubjectFromInventoryState(state);
    if (!subject || isTableFocusSubject(subject)) return false;
    if (subject.focusActionType !== 'ORCHESTRATE_SOURCING') return false;
    return (
      subject.entityType === 'Portfolio' ||
      subject.entityType === 'Property' ||
      subject.entityType === 'Unit'
    );
  });
  // The former Inventory tables now live under the Source tool, so viewing any of
  // them keeps the Source nav item highlighted.
  const isSourceInventoryTable =
    activeTableView === 'properties' ||
    activeTableView === 'brands' ||
    activeTableView === 'contacts' ||
    activeTableView === 'portfolios';
  // ChatGPT's sidebar glyph at its size: a 16.8 x 14.7 rounded panel inside the 20px box, 1.5px
  // strokes, the divider a third in (the old 24-unit drawing filled the box and read heavier).
  const collapseIcon = (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="1.6" y="2.65" width="16.8" height="14.7" rx="3.4" stroke="currentColor" strokeWidth={1.5} />
      <path d="M7.4 2.65V17.35" stroke="currentColor" strokeWidth={1.5} />
    </svg>
  );

  const menuButtonClass = (isActive: boolean) =>
    clsx(
      'group relative flex items-center text-left text-[14px] leading-[14px] text-white transition-colors',
      isCollapsed ? 'mx-auto h-9 w-9 justify-center rounded-[10px]' : 'h-9 w-full gap-1.5 rounded-[10px] px-2.5',
      isActive ? 'bg-[#303030]' : 'hover:bg-[#303030]',
    );


  // The Master agent is not shipped: its nav entries render blurred and inert. One source of
  // truth, so switching it on later is a single edit in lib/access/masterAgentAccess.ts.
  // NOTE: the `isFaterAdmin` prop above is really hasAdminAccess (Fater admin OR organisation
  // admin), so it cannot gate this: a client's own org admin would keep the unreleased agent.
  // masterAgentUnlocked is computed from userContext.isFaterAdmin alone.
  const masterAgentLocked = !masterAgentUnlocked;
  // The Daimon has its own pilot list (lib/access/daimonAccess.ts): her entry and her conversations
  // show for it without opening Master's other surfaces.
  const daimonUnlocked = useKernelStore((state) => canAccessDaimon(state.userContext));
  // Data (the brand referential) is Fater-admin only, like the page it opens in Settings.
  const canOpenData = useKernelStore((state) => state.userContext.isFaterAdmin);
  // Activity is open to organisation admins too, scoped to their organisation (owner, 2026-10-07).
  const canOpenActivity = useKernelStore((state) => state.userContext.isFaterAdmin || state.userContext.isOrganisationAdmin);

  const renderButton = (
    label: string,
    icon: ReactNode,
    onClick: () => void,
    isActive = false,
    buttonId?: string,
    // A surface that exists but is not shipped yet: blurred, inert, and labelled "Coming soon".
    // Used for the Master agent entries (Home / New session / Chats / Doctrine), see
    // lib/access/masterAgentAccess.ts.
    isComingSoon = false,
    // A live entry that carries a label, e.g. Slides showing "Limited Trial" to
    // an evaluation account. Same pill as Coming soon, but the row stays sharp
    // and clickable: it is a status, not a block.
    badge?: string,
    studioTarget?: StudioSection,
  ) => {
    return (
    <button
      type="button"
      id={buttonId}
      data-factory-tour={studioTarget ? `studio-${studioTarget}` : undefined}
      aria-label={label}
      aria-current={isActive ? 'page' : undefined}
      onPointerDown={studioTarget ? event => event.preventDefault() : undefined}
      onClick={isComingSoon ? undefined : onClick}
      disabled={isComingSoon}
      aria-disabled={isComingSoon || undefined}
      className={clsx(
        menuButtonClass(isActive),
        isComingSoon && 'cursor-default',
      )}
      // No native title: the label is written on the row, and the collapsed bar has its own pill.
      style={{ fontFamily: LEFTBAR_FONT_FAMILY }}
    >
      <span
        className={clsx(
          'flex h-5 w-5 shrink-0 items-center justify-center text-white transition-colors',
          // The icon carries the blur too, so the whole row reads as one frosted item.
          isComingSoon && 'blur-[1.6px] opacity-45',
        )}
      >
        {icon}
      </span>
      <span
        className={clsx(
          'overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-400',
          isCollapsed ? 'max-w-0 opacity-0' : 'max-w-[180px] opacity-100',
          isComingSoon && 'blur-[1.6px] opacity-45',
        )}
        style={{ fontSize: '14px', lineHeight: '20px', letterSpacing: '-0.2px', fontFamily: LEFTBAR_FONT_FAMILY, transitionTimingFunction: COLLAPSE_EASE }}
      >
        {label}
      </span>
      {badge && !isComingSoon && !isCollapsed && (
        <span
          className="ml-auto shrink-0 rounded-[5px] border border-[#2A2A2A] bg-[#1B1B1B] px-1.5 py-[3px] text-[10px] leading-[10px] text-[#B5B5B5]"
          style={{ fontFamily: LEFTBAR_FONT_FAMILY }}
        >
          {badge}
        </span>
      )}
      {/* The handle stays SHARP on top of the blurred label: it is the one thing to read. */}
      {isComingSoon && !isCollapsed && (
        <span
          className="ml-auto shrink-0 rounded-[5px] border border-[#2A2A2A] bg-[#1B1B1B] px-1.5 py-[3px] text-[10px] leading-[10px] text-[#B5B5B5]"
          style={{ fontFamily: LEFTBAR_FONT_FAMILY }}
        >
          {MASTER_AGENT_COMING_SOON_LABEL}
        </span>
      )}
      {isCollapsed && (
        <div
          className="pointer-events-none absolute left-full top-1/2 ml-3 -translate-y-1/2 whitespace-nowrap rounded-[999px] bg-[#0B0B0B] px-5 py-3 text-[14px] leading-[14px] text-white opacity-0 shadow-[0_10px_30px_rgba(0,0,0,0.55)] transition-opacity duration-150 group-hover:opacity-100"
          style={{ fontFamily: LEFTBAR_FONT_FAMILY }}
        >
          {isComingSoon ? `${label}, ${MASTER_AGENT_COMING_SOON_LABEL}` : badge ? `${label}, ${badge}` : label}
        </div>
      )}
    </button>
    );
  };

  return (
    <aside
      id="portal-home-side-panel"
      className={clsx(
        'relative h-full shrink-0 bg-black',
        // The single vertical stroke on boxless surfaces; dropped on boxed surfaces
        // where the curved box draws the left edge (box XOR left line).
        showDivider && 'box-content border-r border-white/[0.08]',
        // No width transition while dragging, so the drag tracks the cursor 1:1.
        !isResizing && 'transition-[width] duration-500',
      )}
      style={{ transitionTimingFunction: COLLAPSE_EASE, width: isCollapsed ? 52 : sidebarWidth }}
    >
      {/* Resize handle on the right edge (expanded only). Drag to size; double-click to reset. */}
      {!isCollapsed && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          title="Drag to resize, double-click to reset"
          onMouseDown={startSidebarResize}
          onDoubleClick={resetSidebarWidth}
          className="group absolute inset-y-0 right-0 z-30 w-2 translate-x-1/2 cursor-col-resize"
        >
          <div
            className={clsx(
              'absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors',
              isResizing ? 'bg-[#4A6DA8]' : 'bg-transparent group-hover:bg-[#3A3A3A]',
            )}
          />
        </div>
      )}
      <div className="flex h-full flex-col">
        <div
          className={clsx(
            'relative pb-[10px] pt-[10px] transition-[padding] duration-500',
            isCollapsed ? 'px-2' : 'px-1.5',
          )}
          style={{ transitionTimingFunction: COLLAPSE_EASE }}
        >
          {!isCollapsed && (
            <div className="absolute right-2 top-1 flex items-center">
              <button
                type="button"
                aria-label="Search chats"
                onClick={onSearchChats}
                className="flex h-9 w-9 items-center justify-center rounded-[8px] text-[#CDCDCD] transition-colors hover:bg-[#303030] hover:text-white"
                title="Search chats"
              >
                <SearchIcon size={20} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                aria-label="Collapse menu"
                onClick={onToggleCollapse}
                className="flex h-11 w-11 items-center justify-center rounded-[8px] text-[#CDCDCD] transition-colors hover:bg-[#303030] hover:text-white"
                title="Collapse menu"
              >
                {collapseIcon}
              </button>
            </div>
          )}

          {/* The product names itself, as ChatGPT does (owner, 2026-09-28): the organisation badge and
              switcher that sat here are gone. Collapsed, the Fater mark stands in and turns into the
              expand control on hover. */}
          <div className={clsx('flex h-8 items-center', isCollapsed ? 'justify-center' : 'justify-start pl-3')}>
            {isCollapsed ? (
              <div className="relative h-8 w-8" onMouseEnter={() => setIsOrgHovered(true)} onMouseLeave={() => setIsOrgHovered(false)}>
                <div className={clsx('absolute inset-0 flex items-center justify-center text-white transition-opacity', isOrgHovered ? 'opacity-0' : 'opacity-100')}>
                  <FaterLogo className="h-6 w-6" />
                </div>
                <button
                  type="button"
                  aria-label="Expand menu"
                  title="Expand menu"
                  onClick={onToggleCollapse}
                  className={clsx(
                    'absolute -inset-0.5 flex items-center justify-center rounded-[10px] text-[#E5E5E5] transition-opacity',
                    isOrgHovered ? 'opacity-100 bg-[#1A1A1A]' : 'pointer-events-none opacity-0',
                  )}
                >
                  {collapseIcon}
                </button>
              </div>
            ) : (
              <span className="font-semibold tracking-[-0.01em] text-white" style={{ fontSize: 19, lineHeight: '32px', fontFamily: LEFTBAR_FONT_FAMILY }}>
                Fater AI
              </span>
            )}
          </div>
        </div>

        <div
          className={clsx(
            'transition-[padding] duration-500',
            isCollapsed ? 'px-2 pt-2' : 'px-1.5 pt-2',
          )}
          style={{ transitionTimingFunction: COLLAPSE_EASE }}
        >
          {/* THE DAIMON (owner, 2026-09-28): one entry above the tools replaces Home, New session,
              Chats and Doctrine. It is the voice assistant that explains a user's data and usage and
              gathers feedback; each tool keeps its own doctrine inside the tool. Fater admins only
              until it opens to clients (lib/access/masterAgentAccess.ts). Its conversations are
              listed below, under Pinned and Recents. */}
          <div className="space-y-0">
            {daimonUnlocked && renderButton(
              'Daimon',
              <DaimonOrb size={20} still />,
              startDaimonConversation,
              !activeChatSessionId && !activeTableView && !activeStandaloneView && !daimonSessionId,
              'portal-nav-home',
              false,
            )}
          </div>
        </div>

        {(canAccessDesign || canAccessSource || canAccessAssetManagement) && (
          <div
            className={clsx(
              'pt-0 transition-[padding] duration-500',
              isCollapsed ? 'px-2' : 'px-1.5',
            )}
            style={{ transitionTimingFunction: COLLAPSE_EASE }}
          >
            {(
              <div className="space-y-0">
                {/* A tool is "active" only when you are actually viewing it AND not sitting
                    in a Master session, otherwise a stale tool view (e.g. activeTableView
                    left on 'projects') would light up alongside the session, giving two
                    highlights. On a Master session only the session row is highlighted. */}
                {canAccessDesign && renderButton('Studio Canvas', <StudioCanvasIcon size={ICON_SIZE} aria-hidden="true" />, () => onNavigateToView('projects', 'canvas'), studioActive && studioSection === 'canvas', 'portal-nav-studio-canvas', false, undefined, 'canvas')}
                {canAccessDesign && canAccessFactory && renderButton('Studio List', <StudioListIcon size={ICON_SIZE} aria-hidden="true" />, () => onNavigateToView('projects', 'list'), studioActive && (studioSection === 'list' || studioSection === 'factory'), 'portal-nav-studio-list', false, undefined, 'list')}
                {canAccessDesign && canAccessVector && renderButton('Studio Vector', <StudioVectorIcon size={ICON_SIZE} aria-hidden="true" />, () => onNavigateToView('projects', 'vector'), studioActive && studioSection === 'vector', 'portal-nav-studio-vector', false, undefined, 'vector')}
                {/* Source has no standalone view of its own: it is derived from the session
                    subject, which survives navigation on purpose. Without `!activeStandaloneView`
                    a sourcing subject left over from before kept this row lit next to the tool
                    the user had actually opened - two tools selected at once. Studio carries the
                    same guard, for the same reason. */}
                {canAccessSource && renderButton('Source', <Funnel size={ICON_SIZE} strokeWidth={1.5} />, onSource, (isSourceActive || isSourceInventoryTable) && !isMasterSessionActive && !activeStandaloneView, 'portal-nav-source', false, isSourceTrial ? SOURCE_TRIAL_LABEL : undefined)}
                {canAccessFootfall && renderButton('Merch', <MapIcon size={ICON_SIZE} strokeWidth={1.5} />, () => onNavigateToView('footfall'), activeStandaloneView === 'footfall', 'portal-nav-footfall')}
                {canAccessAssetManagement && renderButton('Analyst', <Table2 size={ICON_SIZE} strokeWidth={1.5} />, () => onNavigateToView('tables'), activeStandaloneView === 'tables' && !isMasterSessionActive, 'portal-nav-tables')}
                {/* Slides, AI-native slide/PDF review editor (RDM design reviews). */}
                {canAccessSlidesTool && renderButton('Slides', <Presentation size={ICON_SIZE} strokeWidth={1.5} />, () => onNavigateToView('slides'), activeStandaloneView === 'slides' && !isMasterSessionActive, 'portal-nav-slides', false, isSlidesTrial ? SLIDES_TRIAL_LABEL : undefined)}
                {/* Fater 3D: the Modelist builds a site's 3D model, live in a 3D space. */}
                {canAccessFater3D && renderButton('3D', <Box size={ICON_SIZE} strokeWidth={1.5} />, () => onNavigateToView('fater3d'), activeStandaloneView === 'fater3d' && !isMasterSessionActive, 'portal-nav-fater3d')}
              </div>
            )}
          </div>
        )}

        {/* Quick access: shortcuts to pages that live in Settings (owner, 2026-09-30: Data in 'some
            kind of quick access section'). The Admin group itself stays in Settings. */}
        {(canOpenData || canOpenActivity) && (
          <div className={clsx('mt-5 shrink-0 transition-[padding] duration-500', isCollapsed ? 'px-2' : 'px-1.5')} style={{ transitionTimingFunction: COLLAPSE_EASE }}>
            {!isCollapsed && <SectionHeading label="Quick access" open={isQuickAccessOpen} onToggle={() => setIsQuickAccessOpen((prev) => !prev)} />}
            {(isQuickAccessOpen || isCollapsed) && canOpenActivity && renderButton('Activity', <Activity size={ICON_SIZE} strokeWidth={1.5} />, () => onNavigateToView('globalActivity'), false, 'portal-nav-activity')}
            {(isQuickAccessOpen || isCollapsed) && canOpenData && renderButton('Data', <Database size={ICON_SIZE} strokeWidth={1.5} />, () => onNavigateToView('brandBase'), false, 'portal-nav-data')}
          </div>
        )}

        <div
          className={clsx(
            'mt-5 flex-1 min-h-0 py-0 transition-[padding] duration-500',
            isCollapsed ? 'px-2' : 'px-1.5',
          )}
          style={{ transitionTimingFunction: COLLAPSE_EASE }}
        >
          {/* The chat list IS the Master agent (now the Daimon): hide it entirely while the agent is
              unreleased, otherwise a client can still open a session from Pinned / Recents. Where it
              shows, it is always there, under every tool, as ChatGPT keeps its chats (owner,
              2026-09-28), replacing the Master-context-only rule of SRC-030. */}
          {(!masterAgentLocked || daimonUnlocked) && !isCollapsed && chatSessionIds.length > 0 && (
            <div className="flex h-full min-h-0 flex-col">
              {/* Pinned: user-pinned sessions, kept at the top. Capped height with its own
                  scroll so a long pin list never crowds out Recents below. */}
              {pinnedChatSessionIds.length > 0 && (
                <div className="flex shrink-0 flex-col">
                  <SectionHeading label="Pinned" open={isPinnedOpen} onToggle={() => setIsPinnedOpen((prev) => !prev)} />
                  {isPinnedOpen && (
                    <div className="custom-scrollbar space-y-0 overflow-y-auto" style={{ maxHeight: '40vh' }}>
                      {pinnedChatSessionIds.map((sessionId) => (
                        <PortalHomeChatSessionItem
                          key={sessionId}
                          sessionId={sessionId}
                          isActive={activeChatSessionId === sessionId || daimonSessionId === sessionId}
                          onOpenSession={openSession}
                          onSessionContextMenu={handleSessionContextMenu}
                          onRenameSession={handleRenameSession}
                          fontFamily={LEFTBAR_FONT_FAMILY}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Recents: everything not pinned, newest first, fills the remaining space. */}
              {recentChatSessionIds.length > 0 && (
                <div data-daimon-tour="recents" className={clsx('flex min-h-0 flex-1 flex-col', pinnedChatSessionIds.length > 0 && 'mt-5')}>
                  <SectionHeading label="Recents" open={isRecentsOpen} onToggle={() => setIsRecentsOpen((prev) => !prev)} />
                  {isRecentsOpen && (
                    <div className="custom-scrollbar min-h-0 flex-1 space-y-0 overflow-y-auto">
                      {recentChatSessionIds.map((sessionId) => (
                        <PortalHomeChatSessionItem
                          key={sessionId}
                          sessionId={sessionId}
                          isActive={activeChatSessionId === sessionId || daimonSessionId === sessionId}
                          onOpenSession={openSession}
                          onSessionContextMenu={handleSessionContextMenu}
                          onRenameSession={handleRenameSession}
                          fontFamily={LEFTBAR_FONT_FAMILY}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <PortalAccountCorner isCollapsed={isCollapsed} />
      </div>
    </aside>
  );
});
PortalHomeMenu.displayName = 'PortalHomeMenu';
